import {
  OutboxEventStatus,
  enumOutboxEventStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Col,
  Row,
  Loading,
  Badge,
  cn,
  humanReadableDateTime,
  DateRangePicker,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideChevronLeft, LucideChevronRight } from 'lucide-react';

import { InventoryEntityFilter } from './InventoryEntityFilter';

import { InventoryOutboxEventRecord } from '@/audit-log/_graphql/queryInventoryOutboxEvents';
import {
  $remoteInventoryOutboxEvents,
  useSelectedInventoryEventId,
  useInventoryPagination,
  useInventoryAggregateTypeFilter,
  useInventoryDateRangeFilter,
} from '@/audit-log/_state/inventoryEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: OutboxEventStatus) => {
  switch (status) {
    case enumOutboxEventStatus.Processed:
      return 'success';
    case enumOutboxEventStatus.Pending:
      return 'warning';
    case enumOutboxEventStatus.Failed:
      return 'failure';
    case enumOutboxEventStatus.DeadLetter:
      return 'failure';
    default:
      return 'neutral';
  }
};

type EventTypeTranslations = {
  item: {
    created: string;
    updated: string;
    deleted: string;
  };
  category: {
    created: string;
    updated: string;
    deleted: string;
  };
  itemGroup: {
    created: string;
    updated: string;
    deleted: string;
  };
  inventoryTag: {
    created: string;
    updated: string;
    deleted: string;
  };
};

const getEventTypeKey = (
  eventName: string,
  payload: string,
): {
  aggregate: keyof EventTypeTranslations;
  action: 'created' | 'updated' | 'deleted';
} | null => {
  try {
    const parsed = JSON.parse(payload);

    let aggregate: keyof EventTypeTranslations | null = null;
    if (eventName === 'ItemEvent') aggregate = 'item';
    else if (eventName === 'CategoryEvent') aggregate = 'category';
    else if (eventName === 'ItemGroupEvent') aggregate = 'itemGroup';
    else if (eventName === 'InventoryTagEvent') aggregate = 'inventoryTag';

    if (!aggregate) return null;

    let action: 'created' | 'updated' | 'deleted' | null = null;
    if (parsed.created) action = 'created';
    else if (parsed.updated) action = 'updated';
    else if (parsed.deleted) action = 'deleted';

    if (!action) return null;

    return { aggregate, action };
  } catch {
    return null;
  }
};

const getTranslatedEventName = (
  eventName: string,
  payload: string,
  eventTypes: EventTypeTranslations,
): string => {
  const key = getEventTypeKey(eventName, payload);
  if (key) {
    return eventTypes[key.aggregate][key.action];
  }
  return eventName;
};

type EventCardProps = {
  event: InventoryOutboxEventRecord;
  isSelected: boolean;
  onClick: () => void;
  idPrefix: string;
  notAvailable: string;
  eventTypes: EventTypeTranslations;
};

const EventCard = ({
  event,
  isSelected,
  onClick,
  idPrefix,
  notAvailable,
  eventTypes,
}: EventCardProps) => (
  <button
    onClick={onClick}
    className={cn(
      '@EventCard',
      cn(
        'w-full rounded-md border p-3 text-left transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-neutral/10 hover:border-neutral/20 hover:bg-neutral/5',
      ),
    )}
  >
    <Col gap2>
      <Row className="justify-between">
        <span className="text-sm font-medium">
          {getTranslatedEventName(event.eventName, event.payload, eventTypes)}
        </span>
        <Badge
          color={getStatusColor(event.status as OutboxEventStatus)}
          size="small"
        >
          {event.status}
        </Badge>
      </Row>
      <Row className="text-xs text-neutral/60" gap2>
        <span>
          {idPrefix} {event.metadata.aggregateId ?? notAvailable}
        </span>
      </Row>
      <Row className="text-xs text-neutral/40">
        <span>
          {humanReadableDateTime(event.timestamps.createdAt, {
            locale: getLocale(),
            includeYear: true,
            includeSeconds: true,
          })}
        </span>
      </Row>
    </Col>
  </button>
);

export const InventoryEventsList = () => {
  const t = useTranslations();
  const [pagination, setPagination] = useInventoryPagination();
  const [selectedEventId, setSelectedEventId] = useSelectedInventoryEventId();
  const [aggregateType] = useInventoryAggregateTypeFilter();
  const [dateRange, setDateRange] = useInventoryDateRangeFilter();

  const { data, isLoading } = useAtomValue($remoteInventoryOutboxEvents);

  const events = data?.events ?? [];
  const total = data?.total ?? 0;

  const hasNextPage = pagination.offset + pagination.limit < total;
  const hasPrevPage = pagination.offset > 0;

  const goToNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  };

  const goToPrevPage = () => {
    setPagination((prev) => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit),
    }));
  };

  if (isLoading && !data) {
    return (
      <Col className="@InventoryEventsList flex-1 items-center justify-center border-r border-neutral/10">
        <Loading isLoading />
      </Col>
    );
  }

  return (
    <Col className="@InventoryEventsList w-[350px] border-r border-neutral/10">
      <Row className="border-b border-neutral/10 p-3">
        <Col className="flex-1">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            aria-label={t.filters.dateRange.label}
            clearable
            onClear={() => setDateRange(null)}
          />
        </Col>
      </Row>
      {aggregateType && (
        <Col className="border-b border-neutral/10 p-3">
          <InventoryEntityFilter />
        </Col>
      )}
      <Col className="flex-1 gap-2 overflow-y-auto p-3">
        {events.length === 0 ? (
          <Col className="flex-1 items-center justify-center text-neutral/40">
            <span>{t.inventoryEventsView.list.empty}</span>
          </Col>
        ) : (
          events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isSelected={selectedEventId === event.id}
              onClick={() => setSelectedEventId(event.id)}
              idPrefix={t.inventoryEventsView.list.idPrefix}
              notAvailable={t.inventoryEventsView.list.notAvailable}
              eventTypes={t.inventoryEventsView.detail.eventTypes}
            />
          ))
        )}
      </Col>

      <Row className="items-center justify-between border-t border-neutral/10 px-3 py-2">
        <span className="text-xs text-neutral/60">
          {pagination.offset + 1} -{' '}
          {Math.min(pagination.offset + pagination.limit, total)} of {total}
        </span>
        <Row gap1>
          <button
            onClick={goToPrevPage}
            disabled={!hasPrevPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasPrevPage
                ? 'hover:bg-neutral/10'
                : 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronLeft size={16} />
          </button>
          <button
            onClick={goToNextPage}
            disabled={!hasNextPage}
            className={cn(
              'rounded p-1 transition-colors',
              hasNextPage
                ? 'hover:bg-neutral/10'
                : 'cursor-not-allowed opacity-30',
            )}
          >
            <LucideChevronRight size={16} />
          </button>
        </Row>
      </Row>
    </Col>
  );
};
